'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import SectionBase from '@/components/ui/sections/section-base';
import SectionHeader from '@/components/ui/sections/section-header';
import {
  TypographyH4,
  TypographyP,
  TypographySmall,
} from '@/components/ui/typography';
import { cls } from '@/lib/utils';

const SECTION_ID = 'service-area-info';

const wrapperMotionProps = {
  initial: 'hidden' as const,
  whileInView: 'visible' as const,
  viewport: { once: true },
  variants: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  },
};

const itemMotionProps = {
  variants: {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  },
};

interface AreaInfoSectionProperties {
  areaInfo: {
    description: string;
    highlights: string[];
    mapEmbedUrl: string;
  };
  townName: string;
}

function AreaInfoSection({ areaInfo, townName }: AreaInfoSectionProperties) {
  return (
    <SectionBase id={SECTION_ID} background="bg-background">
      <motion.div
        className={cls`flex flex-col items-center gap-10`}
        {...wrapperMotionProps}
      >
        <SectionHeader
          subtitle="AREA OVERVIEW"
          title={`About ${townName}, Oklahoma`}
          description={`Why ${townName} is a great place to build your next home.`}
        />

        <div className={cls`grid grid-cols-1 md:grid-cols-2 gap-8 w-full`}>
          <motion.div
            className={cls`flex flex-col gap-6`}
            {...itemMotionProps}
          >
            <TypographyP className="text-foreground leading-relaxed">
              {areaInfo.description}
            </TypographyP>

            <div className="flex flex-col gap-2">
              <TypographyH4 className="text-foreground">
                Area Highlights
              </TypographyH4>
              <ul className="flex flex-col gap-2">
                {areaInfo.highlights.map((highlight) => (
                  <li key={highlight} className="flex items-start gap-2">
                    <CheckCircle className="text-primary size-5 mt-0.5 shrink-0" />
                    <TypographySmall className="text-muted-foreground">
                      {highlight}
                    </TypographySmall>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          <motion.div
            className={cls`w-full min-h-[300px] rounded-lg overflow-hidden`}
            {...itemMotionProps}
          >
            <iframe
              title={`Map of ${townName}, Oklahoma`}
              src={areaInfo.mapEmbedUrl}
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '300px' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
        </div>
      </motion.div>
    </SectionBase>
  );
}

export default AreaInfoSection;
